import React from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiBell, FiShield, FiMessageSquare, FiInfo, FiArrowRight } = FiIcons;

const Contact = () => {
  return (
    <main className="min-h-screen bg-paper pt-24 pb-20">
      <div className="w-full max-w-[1120px] mx-auto px-4">
        {/* Header Section */}
        <header className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-[clamp(2.5rem,5vw,4rem)] mb-6">Contact & Support</h1>
            <p className="text-xl text-muted max-w-[600px] leading-relaxed">
              Have a question about Nisaa? Whether it's about our mission, your data privacy, or the upcoming launch, we're here to help.
            </p>
          </motion.div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Direct Email Column */}
          <motion.section 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-white p-10 rounded-[40px] border border-line shadow-soft relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-rose/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110" />
              
              <div className="w-14 h-14 rounded-2xl bg-aubergine text-white flex items-center justify-center mb-8">
                <SafeIcon icon={FiMail} className="w-7 h-7" />
              </div>
              
              <h2 className="text-2xl font-bold mb-4">Direct Email Support</h2>
              <p className="text-muted mb-8 leading-relaxed">
                Our team monitors this inbox daily. For the fastest response, please include your inquiry type in the subject line.
              </p>
              
              <div className="space-y-4">
                <p className="text-sm font-bold text-aubergine uppercase tracking-widest">Official Email</p>
                <a 
                  href="mailto:contact@sussexendocrineclinic.co.uk" 
                  className="block text-[clamp(1.1rem,3vw,1.6rem)] font-bold text-rose hover:text-aubergine transition-colors break-all"
                >
                  contact@sussexendocrineclinic.co.uk
                </a>
              </div>

              <div className="mt-10 pt-8 border-t border-line flex items-center gap-4 text-sm text-muted">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Typical response time: 24-48 hours
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-6 rounded-3xl bg-white border border-line">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-rose mb-3" />
                <h3 className="font-bold mb-1">Privacy Inquiries</h3>
                <p className="text-xs text-muted">Data export and deletion requests.</p>
              </div>
              <div className="p-6 rounded-3xl bg-white border border-line">
                <SafeIcon icon={FiInfo} className="w-6 h-6 text-rose mb-3" />
                <h3 className="font-bold mb-1">Press & Partnerships</h3>
                <p className="text-xs text-muted">Collaboration and media queries.</p>
              </div>
            </div>
          </motion.section>

          {/* Waitlist Column - Clarified */}
          <motion.section
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-aubergine p-10 lg:p-12 rounded-[40px] text-white shadow-card relative overflow-hidden"
          >
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-rose/10 rounded-full blur-[60px] -mb-32 -mr-32" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose/20 text-rose-soft font-bold text-xs uppercase tracking-wider mb-8">
                <SafeIcon icon={FiBell} className="w-3.5 h-3.5" />
                Launch Updates
              </div>

              <h2 className="text-3xl text-white mb-6">Join the Waitlist</h2>
              <p className="text-white/70 mb-8 leading-relaxed">
                Nisaa is currently in private beta. By joining the waitlist, you aren't just signing up for an email—you're securing early access.
              </p>

              <div className="space-y-6 mb-10">
                <div className="flex gap-4">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-rose flex items-center justify-center mt-1">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Early App Access</p>
                    <p className="text-sm text-white/60">Be among the first to download the app when it hits the stores.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-rose flex items-center justify-center mt-1">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Founding Member Status</p>
                    <p className="text-sm text-white/60">Help shape future features with direct feedback to the team.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-rose flex items-center justify-center mt-1">
                    <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Launch Roadmap</p>
                    <p className="text-sm text-white/60">Get exclusive monthly updates on our development progress.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Button 
                  href="mailto:contact@sussexendocrineclinic.co.uk?subject=Nisaa%20Waitlist%20Request"
                  className="w-full bg-white text-aubergine hover:bg-rose hover:text-white py-4 text-lg"
                >
                  Join the Waitlist
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-[10px] text-center text-white/40 uppercase tracking-widest font-bold">
                  Privacy First: Your email is never shared.
                </p>
              </div>
            </div>
          </motion.section>
        </div>

        {/* FAQ Shortcut */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 p-8 rounded-3xl bg-paper border border-line flex flex-col sm:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center text-rose">
              <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-aubergine">Looking for legal info?</h3>
              <p className="text-sm text-muted">Check our medical and religious disclaimers.</p>
            </div>
          </div>
          <Button variant="secondary" to="/disclaimer">View Disclaimers</Button>
        </motion.div>
      </div>
    </main>
  );
};

export default Contact;